/**
 * SetupWizard — 4-step pre-debate flow.
 *
 *   1. Stratejik Soru — query composer
 *   2. Konsey Yapılandırması — topology + size + rounds + auto-finalize + strictness
 *   3. Konseyi Dinamik Üret — LLM persona generation, edit / regenerate / manual add
 *   4. Önizleme & Başlat — compact summary + Tartışmayı Başlat CTA
 *
 * Honors `mintUserGestureToken` (amendment #1): the final CTA invokes
 * `useStartDebate().start({...})` from a real DOM `click` event.
 *
 * Honors reduced motion (`useReducedMotion`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  FileText,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Rocket,
  Sparkles,
  Wand2,
  Pencil,
  Users,
  Brain,
  Compass,
} from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";

import { useDebateStore } from "../../store/debateStore";
import { useStartDebate } from "../../hooks/useStartDebate";
import { t, tr as i18n } from "../../i18n";
import { useModelsCatalog } from "../../hooks/useModelsCatalog";
import { Card, CardHeader } from "../common/Card";
import { Button } from "../common/Button";
import { Pill } from "../common/Pill";
import { RoleIcon, roleLabel } from "../common/RoleIcon";
import { cn } from "../common/cn";
import { Slider } from "../common/Slider";
import { Switch } from "../common/Switch";
import { SegmentedControl } from "../common/SegmentedControl";
import {
  QueryComposer,
  type QueryComposerSubmitSeed,
} from "../setup/QueryComposer";
import { TopologyPicker } from "../setup/TopologyPicker";
import { RagDropZone } from "../rag/RagDropZone";
import { CouncilSourcePicker } from "./CouncilSourcePicker";
import { CouncilCandidatePicker } from "./CouncilCandidatePicker";
import { isDynamicCouncilSource } from "../../domain/personaLibrary";
import { useResolvedCouncil } from "../../hooks/useResolvedCouncil";
import {
  WIZARD_DEFAULTS,
  WIZARD_LIMITS,
  estimateDebateCost,
  isQualityGateCleared,
  isQualityReportFresh,
  isStepValid,
  type CritiqueStrictness,
  type TerminationProfile,
  type Urgency,
  type WizardConfig,
  type WizardStep,
} from "../../domain/wizard";
import type { AgentRole } from "../../domain/types";

const STEP_TITLES: Record<WizardStep, { label: string; subtitle: string }> = {
  1: { label: "Stratejik Soru", subtitle: "Konseye hangi soruyu soruyorsun?" },
  2: { label: "Konsey Yapılandırması", subtitle: "Topoloji, boyut ve sıkılık." },
  3: { label: "Konseyi Dinamik Üret", subtitle: "LLM uzman seçer; gerekirse düzenle." },
  4: { label: "Önizleme & Başlat", subtitle: "Son kontrol ve tartışmayı başlat." },
};

/* --------------------------------------------------------------------- *
 * Stepper
 * --------------------------------------------------------------------- */

function StepDot({
  step,
  current,
  done,
  enabled,
  onClick,
}: {
  step: WizardStep;
  current: boolean;
  done: boolean;
  enabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      aria-current={current ? "step" : undefined}
      className={cn(
        "focus-ring grid h-8 w-8 flex-none place-items-center rounded-full border text-xs font-semibold transition",
        done
          ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-200"
          : current
            ? "border-[hsl(220_90%_60%/0.7)] bg-[radial-gradient(circle_at_30%_25%,hsl(220_90%_60%/0.6),hsl(265_80%_65%/0.25))] text-fg-strong shadow-[0_0_0_3px_hsl(220_90%_60%/0.18)]"
            : "border-white/10 bg-surface-2/60 text-fg-subtle hover:border-white/20",
        !enabled && "cursor-not-allowed opacity-60",
      )}
    >
      {done ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : step}
    </button>
  );
}

function StepIndicator({
  current,
  jumpTo,
  isStepReachable,
}: {
  current: WizardStep;
  jumpTo(step: WizardStep): void;
  isStepReachable(step: WizardStep): boolean;
}) {
  const steps: WizardStep[] = [1, 2, 3, 4];
  return (
    <div className="flex items-center gap-2 px-4 pt-4 sm:px-5 sm:pt-5">
      {steps.map((s, i) => {
        const meta = STEP_TITLES[s];
        const done = current > s;
        return (
          <div key={s} className="flex flex-1 items-center gap-2">
            <StepDot
              step={s}
              current={current === s}
              done={done}
              enabled={isStepReachable(s)}
              onClick={() => jumpTo(s)}
            />
            <div className="hidden min-w-0 sm:block">
              <div
                className={cn(
                  "truncate text-xs font-semibold",
                  current === s ? "text-fg-strong" : "text-fg-muted",
                )}
              >
                {meta.label}
              </div>
              <div className="truncate text-[10px] uppercase tracking-wider text-fg-subtle">
                Adım {s}/4
              </div>
            </div>
            {i < steps.length - 1 ? (
              <div
                className={cn(
                  "h-px flex-1 rounded-full transition",
                  current > s
                    ? "bg-gradient-to-r from-emerald-400/40 to-emerald-400/10"
                    : "bg-white/[0.06]",
                )}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------------- *
 * Step 2 sub-controls
 * --------------------------------------------------------------------- */

const STRICTNESS_OPTIONS: {
  value: CritiqueStrictness;
  label: string;
  hint: string;
}[] = [
  { value: "loose", label: "Esnek", hint: "3 deneme" },
  { value: "standard", label: "Standart", hint: "2 deneme" },
  { value: "strict", label: "Sert", hint: "1 deneme" },
];

/**
 * Konsey Cognitive Renaissance — Bölüm A0/A1.
 * Erken sonlanma profili — wizard'da segmented control olarak
 * kullanıcıya sunulur. Default `balanced`.
 *   • basit  : finalize_min %30, threshold 0.88 (hızlı brainstorm için)
 *   • orta   : finalize_min %50, threshold 0.92 (önerilen, dengeli)
 *   • sıkı   : finalize_min %75, threshold 0.95 + sycophancy detection
 */
const TERMINATION_PROFILE_OPTIONS: {
  value: TerminationProfile;
  label: string;
  hint: string;
}[] = [
  { value: "loose", label: "Basit", hint: "%30 tur · hızlı kapatma" },
  { value: "balanced", label: "Orta", hint: "%50 tur · dengeli" },
  { value: "strict", label: "Sıkı", hint: "%75 tur · derin müzakere" },
];

/**
 * Görev 4 — urgency override. `auto` is a UI-only sentinel mapped to
 * `null` (let the backend's `question_router` heuristic decide from
 * the query text) — `SegmentedControl` requires a concrete string
 * value per option, so `Urgency | null` can't be used directly.
 */
type UrgencyOption = Urgency | "auto";
const URGENCY_OPTIONS: { value: UrgencyOption; label: string; hint: string }[] = [
  { value: "auto", label: "Otomatik", hint: "Sorudan çıkarılır" },
  { value: "operational", label: "Operasyonel", hint: "Standart derinlik" },
  { value: "crisis", label: "Kriz", hint: "Hızlı · az tur" },
  { value: "strategic", label: "Stratejik", hint: "Derin · çok tur" },
];

/**
 * Konsey Yönergesi şablonları.
 *
 * Operatör geri bildirimi (2026-08): "soru geliştirme için güzel ama
 * soruyu nerede çalışacağı gibi konularda çok yüzeysel kalabiliyor."
 * Boş bir metin kutusu bu boşluğu kapatmıyor — kullanıcı yönergenin ne
 * kadar spesifik olabileceğini bilmiyor. Şablonlar başlangıç noktası
 * verir; metin serbestçe düzenlenebilir.
 */
const DIRECTIVE_PRESETS: { label: string; text: string }[] = [
  {
    label: "Yatırım değerlendirmesi",
    text:
      "Bu bir yatırım kararıdır. Tartışmanın omurgası yüklenen belgelerdeki " +
      "finansal ve ticari veri olmalı: son 3 yılın ciro/kârlılık seyri, satış " +
      "kanalları, müşteri ve sipariş tabanı, ürün portföyü ve üretim/teslimat " +
      "kapasitesi. Teknik yetkinliği yalnızca pazar payını büyütüp " +
      "büyütemeyeceği ölçüsünde tartışın — mimari ya da bileşen düzeyinde " +
      "teknik derinliğe İNMEYİN. Sonuçta net bir yatırım tavsiyesi isterim: " +
      "yatırım yapılmalı mı, hangi değerleme aralığında, hangi somut " +
      "koşullara bağlı ve kararı bozacak eşikler neler.",
  },
  {
    label: "Pazar ve rekabet analizi",
    text:
      "Odak pazar konumlandırması olsun: hedef segmentler, ulaşılabilir pazar " +
      "büyüklüğü, rakiplerin fiyat ve kabiliyet karşılaştırması, giriş " +
      "bariyerleri ve farklılaşma iddiasının kanıtı. Her iddiayı sayıyla " +
      "destekleyin; belgelerde sayı yoksa bunu açıkça söyleyin.",
  },
  {
    label: "Teknik fizibilite",
    text:
      "Odak uygulanabilirlik olsun: mevcut yetkinlikler, teknik borç, " +
      "entegrasyon riskleri, gerekli ekip ve süre. Her teknik iddiayı " +
      "maliyet ve takvim etkisine bağlayın; sonuçta yapılabilir/yapılamaz " +
      "yargısı ve bunun bedeli net olsun.",
  },
  {
    label: "Risk ve uyum",
    text:
      "Odak risk olsun: regülasyon ve uyum yükümlülükleri, tedarik ve " +
      "bağımlılık riskleri, en kötü senaryolar ve bunların ölçülebilir " +
      "etkisi. Her riski olasılık, etki ve somut bir azaltma aksiyonuyla " +
      "birlikte verin.",
  },
  {
    label: "Sade dil",
    text:
      "Alan jargonundan kaçının. Kullanılan her teknik terimi ilk geçtiği " +
      "yerde bir cümleyle açıklayın. Sonuçlar teknik olmayan bir yöneticinin " +
      "tek okumada anlayacağı düzeyde yazılsın.",
  },
];

/* --------------------------------------------------------------------- *
 * Step 3 — persona cards & generation panel
 * --------------------------------------------------------------------- */

// ─── Seat colours — each orbiting dot represents a future council member ────
const SEAT_PALETTE = [
  "hsl(195 100% 60%)",  // cyan
  "hsl(258 85% 68%)",   // violet
  "hsl(142 65% 55%)",   // emerald
  "hsl(35  90% 58%)",   // amber
  "hsl(355 80% 65%)",   // rose
  "hsl(200 90% 65%)",   // sky
  "hsl(290 70% 65%)",   // purple
  "hsl(170 75% 52%)",   // teal
  "hsl(22  95% 62%)",   // orange
  "hsl(240 75% 68%)",   // indigo
] as const;

const THINKING_LINES = [
  "Uzmanlık alanları belirleniyor…",
  "Bilişsel çeşitlilik hesaplanıyor…",
  "Konsey kimlikler oluşturuluyor…",
  "Argüman çerçeveleri yükleniyor…",
  "Perspektifler dengeleniyor…",
] as const;

/**
 * Full-bleed animated loader shown while the wizard's LLM generates personas.
 * Renders a neural-network / "AI brain council" visual:
 *   — Central brain core with pulsing rings
 *   — Orbiting seat dots (one per expected council member) that glow in as
 *     they're assigned a persona
 *   — Animated SVG synapses drawn between active seats
 *   — Cycling status text phrases
 *   — Ghost skeleton cards below (partially visible) to hint at the layout
 */
function CouncilForgeLoader({ count }: { count: number }) {
  const seats = Math.max(2, Math.min(count, 10));
  const reducedMotion = useReducedMotion();

  // ── Cycling text ─────────────────────────────────────────────────────────
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [textVisible, setTextVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setTextVisible(false);
      setTimeout(() => {
        setPhraseIdx((i) => (i + 1) % THINKING_LINES.length);
        setTextVisible(true);
      }, 300);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  // ── Geçen süre sayacı ────────────────────────────────────────────────────
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const t = setInterval(
      () => setElapsedSec(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => clearInterval(t);
  }, []);

  // ── Dot counter: seats light up one-by-one ───────────────────────────────
  const [litSeats, setLitSeats] = useState(1);
  useEffect(() => {
    if (litSeats >= seats) return;
    const t = setTimeout(
      () => setLitSeats((n) => Math.min(n + 1, seats)),
      420 + Math.random() * 280,
    );
    return () => clearTimeout(t);
  }, [litSeats, seats]);

  const CORE_SIZE = 88;          // px — central brain sphere
  const ORBIT_R   = 108;         // px — radius of seat orbit
  const SEAT_SIZE = 22;          // px — diameter of each seat dot

  // Pre-compute each seat's angle so SVG synapses can reference them
  const seatAngles = Array.from({ length: seats }, (_, i) =>
    (i * 360) / seats,
  );

  // Convert polar → cartesian for SVG synapse endpoints
  const polar = (angleDeg: number, r: number) => ({
    x: 50 + r * Math.cos((angleDeg * Math.PI) / 180),
    y: 50 + r * Math.sin((angleDeg * Math.PI) / 180),
  });

  // Dot bouncer positions for the "thinking" indicator
  const dots = [0, 1, 2];

  return (
    <div className="flex flex-col items-center gap-6 py-4 select-none" aria-busy="true">

      {/* ── Central neural sphere ─────────────────────────────────────────── */}
      <div
        className="relative flex items-center justify-center"
        style={{ width: CORE_SIZE + ORBIT_R * 2 + SEAT_SIZE, height: CORE_SIZE + ORBIT_R * 2 + SEAT_SIZE }}
      >
        {/* Ambient halo rings */}
        {!reducedMotion && (
          <>
            {[1.55, 1.3, 1.1].map((scale, i) => (
              <div
                key={i}
                className="absolute inset-0 rounded-full border border-accent-400/20"
                style={{
                  transform: `scale(${scale})`,
                  animation: `nc-halo ${2.8 + i * 0.6}s ease-out infinite`,
                  animationDelay: `${i * 0.9}s`,
                  opacity: 0,
                }}
              />
            ))}
          </>
        )}

        {/* Rotating outer ring */}
        <div
          className="absolute rounded-full"
          style={{
            width: CORE_SIZE + ORBIT_R * 2 - 8,
            height: CORE_SIZE + ORBIT_R * 2 - 8,
            background: "conic-gradient(from 0deg, hsl(195 100% 60% / 0.0) 0%, hsl(195 100% 60% / 0.18) 25%, hsl(258 80% 65% / 0.12) 50%, hsl(195 100% 60% / 0.0) 75%)",
            animation: reducedMotion ? "none" : "nc-ring-cw 7s linear infinite",
          }}
        />
        {/* Counter-rotating inner ring */}
        <div
          className="absolute rounded-full"
          style={{
            width: CORE_SIZE + ORBIT_R * 1.3,
            height: CORE_SIZE + ORBIT_R * 1.3,
            background: "conic-gradient(from 180deg, hsl(170 90% 55% / 0.0) 0%, hsl(170 90% 55% / 0.14) 30%, hsl(210 85% 65% / 0.09) 60%, hsl(170 90% 55% / 0.0) 90%)",
            animation: reducedMotion ? "none" : "nc-ring-ccw 5s linear infinite",
          }}
        />

        {/* SVG synapse layer — animated strokes between active seat pairs */}
        <svg
          className="absolute inset-0"
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          style={{ overflow: "visible" }}
          aria-hidden
        >
          {seatAngles.slice(0, litSeats).map((a, i) => {
            if (i === 0) return null;
            const from = polar(seatAngles[i - 1]!, ORBIT_R / (CORE_SIZE / 2 + ORBIT_R));
            const to   = polar(a,                  ORBIT_R / (CORE_SIZE / 2 + ORBIT_R));
            const cx   = (from.x + to.x) / 2 + (Math.random() - 0.5) * 10;
            const cy   = (from.y + to.y) / 2 + (Math.random() - 0.5) * 10;
            return (
              <path
                key={i}
                d={`M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`}
                fill="none"
                stroke={`hsl(195 100% 65% / 0.35)`}
                strokeWidth="0.5"
                strokeDasharray="200"
                strokeDashoffset="0"
                style={{
                  animation: reducedMotion
                    ? "none"
                    : `nc-synapse ${1.8 + i * 0.4}s ease-in-out infinite`,
                  animationDelay: `${i * 0.25}s`,
                }}
              />
            );
          })}
        </svg>

        {/* Orbiting seat dots */}
        {seatAngles.map((angleDeg, i) => {
          const color = SEAT_PALETTE[i % SEAT_PALETTE.length] ?? "hsl(195 100% 60%)";
          const isLit = i < litSeats;
          return (
            <div
              key={i}
              className="nc-seat"
              style={{
                "--seat-r": `${ORBIT_R}px`,
                "--seat-start": `${angleDeg}deg`,
                "--seat-duration": `${14 + (i % 3) * 3}s`,
                "--seat-size": `${SEAT_SIZE}px`,
                "--seat-color": `${color.replace(")", " / 0.6)").replace("hsl(", "hsl(")}`,
                background: isLit ? color : "hsl(220 20% 18%)",
                border: `1.5px solid ${isLit ? color : "hsl(220 25% 28%)"}`,
                opacity: isLit ? 1 : 0.35,
                transition: "background 0.4s ease, opacity 0.4s ease",
                boxShadow: isLit
                  ? `0 0 10px 3px ${color.replace(")", " / 0.55)").replace("hsl(", "hsl(")}`
                  : "none",
              } as React.CSSProperties}
            >
              {/* Micro-label index */}
              <span
                className="absolute inset-0 flex items-center justify-center text-[7px] font-bold"
                style={{ color: isLit ? "white" : "hsl(220 20% 45%)" }}
              >
                {i + 1}
              </span>
            </div>
          );
        })}

        {/* Central brain core */}
        <div
          className="relative z-10 flex flex-col items-center justify-center rounded-full"
          style={{
            width: CORE_SIZE,
            height: CORE_SIZE,
            background:
              "radial-gradient(circle at 38% 36%, hsl(210 80% 28%), hsl(220 30% 8%) 65%)",
            border: "1.5px solid hsl(195 100% 60% / 0.35)",
            boxShadow:
              "0 0 20px 6px hsl(195 100% 60% / 0.12), inset 0 1px 0 hsl(195 100% 80% / 0.1)",
            animation: reducedMotion ? "none" : "nc-pulse 3s ease-in-out infinite",
          }}
        >
          <Brain
            className="h-7 w-7"
            style={{ color: "hsl(195 100% 72%)" }}
            strokeWidth={1.3}
          />
          {/* Seat count badge */}
          <div
            className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold"
            style={{ color: "hsl(195 100% 75%)" }}
          >
            <span>{litSeats}</span>
            <span style={{ color: "hsl(220 20% 45%)" }}>/</span>
            <span>{seats}</span>
          </div>
        </div>
      </div>

      {/* ── Status text ───────────────────────────────────────────────────── */}
      <div className="flex flex-col items-center gap-2">
        <div className="h-5 overflow-hidden">
          <p
            className="text-sm font-medium text-fg-muted transition-all duration-300"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? "translateY(0)" : "translateY(-6px)",
            }}
          >
            {THINKING_LINES[phraseIdx]}
          </p>
        </div>

        {/* Dot bounce loader */}
        <div className="flex gap-1.5">
          {dots.map((i) => (
            <div
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-accent-400"
              style={{
                animation: reducedMotion ? "none" : `nc-dot-bounce 1.4s ease-in-out infinite`,
                animationDelay: `${i * 0.22}s`,
              }}
            />
          ))}
        </div>

        {/*
          Geçen süre — döngüsel metin ve yanıp sönen noktalar tek başına
          "sistem yaşıyor mu" sorusunu YANITLAMIYOR: ikisi de üretim
          takılsa bile aynen dönmeye devam eder. Operatör raporu
          (2026-08-02): 8 üyelik konseyde ekran 10 dakika bu hâlde kaldı
          ve kullanıcı ilerleme olup olmadığını anlayamadı.

          Eşik, sunucudaki GERÇEK dağılıma göre kalibre edilmiştir
          (2026-08-04 ölçümü, 17 sağlıklı koşu: medyan 34 sn, p90 48 sn,
          en yavaş 72 sn). Önceki "20-40 sn tipik / 60 sn'de uyar" beyanı
          bunun altındaydı, dolayısıyla tamamen sağlıklı koşuların bir
          kısmı kırmızıya boyanıp operatöre sistem takıldı izlenimi
          veriyordu — raporlanan "ilerlemiyor" şikâyetinin bir kısmı
          buydu. Uyarı artık yalnızca ölçülen en yavaş koşunun ötesinde
          çıkar.

          Sayaç, isteğin kendi zaman aşımıyla AYNI saati kullanmaz (birkaç
          saniye önce başlar), bu yüzden metin kesin bir an vaat etmez.
          Eskiden "120 sn'de otomatik durup hatayı gösterecek" yazıyordu ve
          bu söz tutulmuyordu: istemci tavanı yalnızca `fetch` çağrısını
          kapsıyor, yanıt gövdesinin okunmasını kapsamıyordu. Uzak ağ /
          VPN üzerinde başlıklar zamanında gelip gövde yarıda takılınca
          hiçbir zaman aşımı tetiklenmiyordu — operatör 128 sn'de hâlâ bu
          ekranı görüyordu. Tavan artık gövdeyi de kapsıyor
          (`wizardClient.requestJson`).
        */}
        <p
          className={cn(
            "text-[11px] tabular-nums transition-colors",
            elapsedSec >= 75 ? "text-amber-300/90" : "text-fg-subtle",
          )}
          aria-live="polite"
        >
          {elapsedSec} sn geçti
          {elapsedSec >= 75
            ? " — ölçülen en yavaş koşuyu aştı; yaklaşık 2 dakikada isteği kesip hatayı gösterir."
            : " · tipik süre 30-50 sn, konsey büyüdükçe uzar"}
        </p>
      </div>

      {/* ── Ghost skeleton cards (bottom) ────────────────────────────────── */}
      <div className="w-full grid gap-2.5 sm:grid-cols-2 opacity-30 pointer-events-none">
        {Array.from({ length: Math.min(seats, 4) }, (_, i) => (
          <div
            key={i}
            className="nc-shimmer flex flex-col gap-2 rounded-xl border border-white/[0.05] p-3"
          >
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 rounded-full bg-white/8 ring-1 ring-white/10" />
              <div className="flex-1 space-y-1">
                <div className="h-2.5 w-1/2 rounded bg-white/10" />
                <div className="h-2 w-1/4 rounded bg-white/6" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="h-1.5 w-full rounded bg-white/6" />
              <div className="h-1.5 w-3/4 rounded bg-white/4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface PersonaCardProps {
  id: string;
  role: AgentRole;
  displayName: string;
  expertise?: string | undefined;
  stanceSummary: string;
  themeColor: string;
  seatIndex: number;
  modelLabel?: string | undefined;
  onRegenerate: () => void;
  onEdit: () => void;
  onRemove?: (() => void) | undefined;
  busy?: boolean;
}

function PersonaCard({
  role,
  displayName,
  expertise,
  stanceSummary,
  themeColor,
  seatIndex,
  modelLabel,
  onRegenerate,
  onEdit,
  onRemove,
  busy,
}: PersonaCardProps) {
  // `expertise` is the real-world job title and the PRIMARY badge.
  // The abstract `role` (cognitive disposition) only shows as the icon
  // and a small subscript so cards stay scannable.
  const primary = expertise?.trim() || roleLabel(role);
  return (
    <article
      className={cn(
        "glass relative flex flex-col gap-2 rounded-xl border border-white/[0.07] p-3 transition",
        "hover:border-white/[0.14] hover:shadow-[0_18px_44px_-22px_hsl(var(--persona-glow)/0.5)]",
      )}
      style={{ ["--persona-glow" as string]: themeColor }}
    >
      <header className="flex items-start gap-3">
        <div
          aria-hidden
          className="grid h-9 w-9 flex-none place-items-center rounded-full border border-[hsl(var(--persona-glow)/0.65)] bg-[radial-gradient(circle_at_30%_25%,hsl(var(--persona-glow)/0.55),hsl(var(--persona-glow)/0.18))] text-white"
          title={roleLabel(role)}
        >
          <RoleIcon role={role} className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-semibold text-fg-strong">
              {displayName}
            </span>
            <Pill
              className="max-w-[180px] truncate text-[10px] uppercase tracking-wider"
              title={primary}
            >
              {primary}
            </Pill>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-subtle">
            <span>Koltuk {seatIndex + 1}</span>
            {expertise ? (
              <>
                <span aria-hidden className="opacity-50">·</span>
                <span className="opacity-70">{roleLabel(role)}</span>
              </>
            ) : null}
            {/* Konsey koltukları farklı modellere dağıtılıyor ama bu hiçbir
                yerde görünmüyordu; operatörler haklı olarak "hepsi aynı LLM'i
                mi kullanıyor?" diye sordu. Modeli koltuğun yanında göstermek,
                dağılımı düzenleme çekmecesini açmadan doğrulanabilir kılar. */}
            {modelLabel ? (
              <>
                <span aria-hidden className="opacity-50">·</span>
                <span
                  className="max-w-[120px] truncate opacity-70"
                  title={`Bu koltuğun modeli: ${modelLabel}`}
                >
                  {modelLabel}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            title="Düzenle"
            className="focus-ring rounded-md p-1.5 text-fg-subtle hover:bg-white/[0.05] hover:text-fg-strong"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onRegenerate}
            disabled={busy}
            title="Bu koltuğu yeniden üret"
            className="focus-ring rounded-md p-1.5 text-fg-subtle hover:bg-white/[0.05] hover:text-fg-strong disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
          </button>
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              title="Sil"
              className="focus-ring rounded-md p-1.5 text-rose-300/70 hover:bg-white/[0.05] hover:text-rose-300"
            >
              ×
            </button>
          ) : null}
        </div>
      </header>
      <p className="line-clamp-3 text-xs leading-snug text-fg-muted">
        {stanceSummary || "Stance özeti henüz yok."}
      </p>
    </article>
  );
}

/* --------------------------------------------------------------------- *
 * Step 3 body — standalone component
 *
 * Why this is its own component (not inlined inside SetupWizard's
 * conditional ternary):
 *
 *   1. INVARIANT: this panel MUST never render blank. The previous
 *      inlined version used a nested ternary where ONE branch (`else`)
 *      had to cover idle, error, and post-clear states; an unexpected
 *      truthy combination of `isGenerating + personas.length > 0`
 *      could fall through the ternary entirely and the whole panel
 *      collapsed to zero height (operator-reported 2026-05-02
 *      screenshot: header visible, body empty across all topologies).
 *
 *   2. The fix is to materialise the panel as THREE INDEPENDENT
 *      regions and render WHICHEVER ONES ARE NON-EMPTY:
 *
 *         ┌──────────────────────────────────┐
 *         │ Header + actions (always render) │
 *         ├──────────────────────────────────┤
 *         │ Inline banners (error, undersize)│  ← optional, may be empty
 *         ├──────────────────────────────────┤
 *         │ Body picker (cards | skeleton | empty-state)
 *         │   — exactly ONE of these renders, decided by a `match`
 *         │     on a single discriminant `kind: 'cards'|'skeleton'|'empty'`
 *         └──────────────────────────────────┘
 *
 *   3. This makes the dead-state machine impossible to reach via
 *      ternary mis-nesting and gives us a single inspectable
 *      `console.warn` if any unexpected condition produces an
 *      undefined `kind` (canary for future regressions).
 * --------------------------------------------------------------------- */

interface Step3BodyProps {
  wizard: WizardConfig;
  isLocked: boolean;
  busyPersonaId: string | null;
  onGenerate: () => Promise<void>;
  onRegenerateOne: (id: string) => Promise<void>;
  onRemove: (id: string) => void;
  onEditOpen: (id: string) => void;
  onJumpToQuery: () => void;
}

function Step3Body({
  wizard,
  isLocked,
  busyPersonaId,
  onGenerate,
  onRegenerateOne,
  onRemove,
  onEditOpen,
  onJumpToQuery,
}: Step3BodyProps) {
  // Koltuk kartlarında model adını gösterebilmek için katalog. Tek seferlik
  // paylaşılan fetch; katalog gelmezse kart eskisi gibi modelsiz görünür.
  const modelCatalog = useModelsCatalog();
  const modelLabelFor = (modelId?: string | null): string | undefined => {
    if (!modelId) return undefined;
    const hit = modelCatalog.models.find((m) => m.id === modelId);
    return hit?.label ?? modelId;
  };

  // ── Defensive coercion of every input that drives the layout ──
  const personas = Array.isArray(wizard.personas) ? wizard.personas : [];
  const safeAgentCount =
    Number.isFinite(wizard.agentCount) && wizard.agentCount > 0
      ? Math.min(20, Math.floor(wizard.agentCount))
      : WIZARD_DEFAULTS.agentCount;
  const expectedCount = safeAgentCount;
  const isGenerating = wizard.generationStatus === "generating";
  const querySatisfied =
    wizard.query.trim().length >= WIZARD_LIMITS.queryMin;
  const undersized =
    !isGenerating && personas.length > 0 && personas.length < expectedCount;

  // ── Discriminant — exactly ONE body state at a time ──
  // Order is significant: we prefer to show real cards if we have any,
  // then loading skeletons, otherwise the empty-state. This is exhaustive
  // by construction (`empty` is the default), so Step 3 cannot render
  // a zero-height body even if the upstream state combination is novel.
  type BodyKind = "cards" | "skeleton" | "empty";
  let kind: BodyKind;
  if (personas.length > 0) {
    kind = "cards";
  } else if (isGenerating) {
    kind = "skeleton";
  } else {
    kind = "empty";
  }

  // Diagnostic canary — if we ever fall back to `empty` while the
  // operator clearly expects content (query is OK, generation is in
  // flight), surface it to the console so the next operator who sees
  // an empty panel can hand us a reproducible log line.
  useEffect(() => {
    if (kind === "empty" && querySatisfied && isGenerating) {
      // eslint-disable-next-line no-console
      console.warn(
        "[wizard.step3] empty body reached despite isGenerating=true — " +
          "investigate state machine: " +
          JSON.stringify({
            personasLen: personas.length,
            generationStatus: wizard.generationStatus,
            agentCount: wizard.agentCount,
          }),
      );
    }
  }, [kind, querySatisfied, isGenerating, personas.length, wizard.generationStatus, wizard.agentCount]);

  // Skeleton count — clamped at [1, 20] so a corrupted agentCount
  // (NaN / negative / huge) cannot produce an empty grid.
  const skeletonCount = Math.max(1, Math.min(20, expectedCount));

  // Persona Kütüphanesi — konsey kaynağı açıkça seçilir. Dinamik üretim
  // bölümü YALNIZCA "auto" seçildiğinde görünür; iki kaynağı yan yana
  // göstermek "hangisi masaya oturacak?" belirsizliği üretiyor.
  // Elimizde zaten bir konsey varsa (fork, geri gelme, manuel ekleme) kaynak
  // sorusu anlamsızdır — "auto" varsayılır. Bu, kaynak alanını bilmeyen eski
  // bir kod yolunun personaları gizlemesine karşı ikinci savunma hattı.
  const source: typeof wizard.councilSource =
    wizard.councilSource ?? (personas.length > 0 ? "auto" : null);
  const librarySelected =
    Boolean(wizard.libraryTeamId) ||
    Boolean(wizard.libraryUnitId) ||
    (wizard.libraryPersonaIds?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-4">
      <CouncilSourcePicker {...(isLocked ? { disabled: true } : {})} />

      {source === "catalog" ? <CouncilCandidatePicker disabled={isLocked} /> : null}

      {source !== null && !isDynamicCouncilSource(source) ? (
        librarySelected ? (
          <div className="rounded-xl border border-accent-300/30 bg-[hsl(var(--accent)/0.05)] px-4 py-3 text-[12px] leading-relaxed text-fg-muted">
            Kalıcı konsey seçildi. Bu üyeler geçmiş tartışmalardan hatırladıklarıyla,
            kişisel belge arşivleriyle ve senin geri bildirimlerinden öğrendikleri
            davranış kurallarıyla masaya oturacak. Dinamik uzman üretimi atlanıyor —
            doğrudan son adıma geçebilirsin.
          </div>
        ) : (
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3 text-[12px] leading-relaxed text-amber-200">
            Yukarıdan bir seçim yap; devam edebilmek için en az bir persona
            gerekiyor.
          </div>
        )
      ) : source === null ? (
        <div className="rounded-xl border border-line-soft/50 bg-surface-2/20 px-4 py-3 text-[12px] leading-relaxed text-fg-subtle">
          Devam etmek için yukarıdan konseyin nasıl kurulacağını seç.
        </div>
      ) : (
        <>
      {/* ── REGION 1: header + actions ──
        * Katalog kolunda üretim düğmeleri CouncilCandidatePicker'a aittir;
        * burada ikinci bir "Konseyi Üret" göstermek hangi düğmenin kadroyu
        * kuracağını belirsizleştirir. Kadro kurulmadan başlık da gereksiz.
        */}
      {source === "catalog" ? (
        personas.length > 0 ? (
          <div>
            <div className="text-sm font-semibold text-fg-strong">
              Konsey üyeleri
              <span className="ml-2 rounded-full border border-white/10 bg-surface-2/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
                {personas.length}
              </span>
            </div>
            <div className="text-xs text-fg-subtle">
              Seçtiğin adaylardan kuruldu. Her üyeyi düzenleyebilirsin.
            </div>
          </div>
        ) : null
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-fg-strong">
              Konsey üyeleri
              {personas.length > 0 ? (
                <span className="ml-2 rounded-full border border-white/10 bg-surface-2/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
                  {personas.length}/{expectedCount}
                </span>
              ) : null}
            </div>
            <div className="text-xs text-fg-subtle">
              Sorunuzun bağlamından {expectedCount} uzman tasarlanacak.
            </div>
          </div>
          <div className="flex items-center gap-2">
            {personas.length > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onGenerate}
                disabled={isLocked || isGenerating}
              >
                <RefreshCw className="h-4 w-4" /> Tümünü yeniden üret
              </Button>
            ) : null}
            <Button
              variant="primary"
              onClick={onGenerate}
              disabled={isLocked || isGenerating || !querySatisfied}
              size="md"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Üretiliyor…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" /> Konseyi Üret
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ── REGION 2: inline banners — render whichever apply ── */}
      {wizard.generationError ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <span className="leading-relaxed">{wizard.generationError}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onGenerate}
            disabled={isLocked || isGenerating || !querySatisfied}
          >
            <RefreshCw className="h-4 w-4" /> Tekrar dene
          </Button>
        </div>
      ) : null}

      {undersized ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
          <span className="leading-relaxed">
            {expectedCount} üye istendi, {personas.length} üye üretildi. Eksik
            koltukları tamamla veya konseyi yeniden üret.
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onGenerate}
            disabled={isLocked || isGenerating}
          >
            <RefreshCw className="h-4 w-4" /> Tümünü yeniden üret
          </Button>
        </div>
      ) : null}

      {/* ── REGION 3: body — exactly ONE of cards / skeleton / empty ── */}
      {kind === "cards" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {personas.map((p) => {
            // Guard against null/undefined entries that would crash
            // PersonaCard's prop spread.
            if (!p || typeof p !== "object" || !p.id) return null;
            return (
              <PersonaCard
                key={p.id}
                id={p.id}
                role={p.role}
                displayName={p.displayName ?? "—"}
                expertise={p.expertise}
                stanceSummary={p.stanceSummary ?? ""}
                themeColor={p.themeColor ?? "hsl(220 70% 56%)"}
                seatIndex={p.seatIndex ?? 0}
                modelLabel={modelLabelFor(p.modelId)}
                busy={busyPersonaId === p.id}
                onRegenerate={() => void onRegenerateOne(p.id)}
                onEdit={() => onEditOpen(p.id)}
                onRemove={
                  personas.length > expectedCount
                    ? () => onRemove(p.id)
                    : undefined
                }
              />
            );
          })}
        </div>
      ) : kind === "skeleton" ? (
        <CouncilForgeLoader count={skeletonCount} />
      ) : (
        // empty: covers idle, error, post-clear, "query too short"
        <div
          className={cn(
            "glass relative grid place-items-center gap-3 rounded-2xl",
            "border border-dashed border-white/[0.10] px-6 py-12 text-center",
          )}
        >
          <div
            aria-hidden
            className="grid h-12 w-12 place-items-center rounded-full border border-accent-400/40 bg-accent-500/15 text-accent-200"
          >
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="max-w-md space-y-1.5">
            <div className="text-sm font-semibold text-fg-strong">
              {!querySatisfied
                ? "Önce stratejik soruyu netleştir"
                : wizard.generationStatus === "error"
                  ? "Konsey üretilemedi — yeniden dene"
                  : "Konsey henüz üretilmedi"}
            </div>
            <div className="text-xs leading-relaxed text-fg-muted">
              {!querySatisfied
                ? "1. adıma dön ve en az 12 karakterlik somut bir soru yaz; uzman seçimi sorudan türetilir."
              : "LLM, savunma sanayi bağlamına uygun gerçek meslek unvanlarına sahip uzmanları üretecek (C4ISR Mimarı, Aviyonik Baş Mühendisi, Tedarik Zinciri ve Yerlileştirme Müdürü vb.). Üretim 10–25 sn sürer."}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!querySatisfied ? (
              <Button variant="ghost" size="sm" onClick={onJumpToQuery}>
                <ArrowLeft className="h-4 w-4" /> Soruyu düzenle
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                onClick={onGenerate}
                disabled={isLocked || isGenerating}
              >
                <Sparkles className="h-4 w-4" /> Konseyi Üret
              </Button>
            )}
          </div>
        </div>
      )}

      <ManualPersonaAdder />
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- *
 * Persona edit drawer
 * --------------------------------------------------------------------- */

const ROLE_OPTIONS: { value: AgentRole; label: string }[] = [
  { value: "moderator", label: "Moderatör" },
  { value: "strategic-visionary", label: "Stratejik Vizyoner" },
  { value: "technical-authority", label: "Teknik Otorite" },
  { value: "threat-analyst", label: "Tehdit ve Risk Analisti" },
  { value: "contrarian", label: "Muhalif Ses" },
  { value: "operational-commander", label: "Operasyonel Yönetici" },
  { value: "compliance-officer", label: "Uyum ve Regülasyon" },
];

/**
 * ManualPersonaAdder — inline composer that turns a free-form job title
 * into a fully-fleshed persona via the backend LLM (`/persona/from-title`).
 *
 * Why this lives in the wizard step rather than as a Drawer:
 *   • Discoverability — sits right next to "Manuel ekle" so both paths
 *     are visible. The classic add-then-edit flow stays for power users
 *     who want to type every field by hand.
 *   • Latency UX — the title-based flow is one network round-trip; an
 *     inline form with a spinner feels native, whereas a modal would feel
 *     like a context switch.
 */
function ManualPersonaAdder() {
  const wizard = useDebateStore((s) => s.wizard);
  const manualAdd = useDebateStore((s) => s.manualAdd);
  const addPersonaFromTitle = useDebateStore((s) => s.addPersonaFromTitle);

  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onAi = useCallback(async () => {
    setErr(null);
    setBusy(true);
    const t = toast.loading(`Persona üretiliyor: ${title.trim()}…`);
    try {
      await addPersonaFromTitle(title.trim(), wizard.query);
      setTitle("");
      toast.success("Konsey üyesi eklendi.", { id: t });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      toast.error(`Persona üretilemedi: ${msg}`, { id: t });
    } finally {
      setBusy(false);
    }
  }, [addPersonaFromTitle, title, wizard.query]);

  const canSubmit = title.trim().length >= 2 && !busy;

  return (
    <div className="glass rounded-xl border border-white/[0.06] p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Konsey üyesi ekle
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setErr(null);
            manualAdd();
          }}
          disabled={busy}
        >
          <Plus className="h-4 w-4" /> Boş kart
        </Button>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <div className="relative flex-1">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) {
                e.preventDefault();
                void onAi();
              }
            }}
            placeholder="Unvan yaz: ör. Aviyonik Sistemler Baş Mühendisi"
            maxLength={120}
            className="focus-ring w-full rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-sm text-fg-strong placeholder:text-fg-subtle"
            disabled={busy}
          />
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={onAi}
          disabled={!canSubmit}
          className="shrink-0"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Yapay Zekâ ile Üret
        </Button>
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px]">
        <span className="text-fg-subtle">
          LLM unvanı, sistem yönergesini, kaynakları ve kör noktaları yazar.
        </span>
        {err ? <span className="text-rose-300">{err}</span> : null}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- *
 * PersonaRagAccordion — Step-4 inline per-persona RAG uploaders
 *
 * Each council member gets a collapsible row whose body is a scoped
 * `RagDropZone` (`personaId={p.id}`). Collapsed rows render as a single
 * line so a 6-member council doesn't blow up the wizard height; the body
 * is materialised lazily (via conditional render) so we don't fire 6
 * `listDocuments` requests in parallel just because the user reached
 * Step 4 — only the rows the operator actually opens hit the API.
 *
 * We deliberately avoid `<details>` here so the expanded block has the
 * same glassmorphism treatment as the rest of the wizard (browsers
 * default `<details>` to the system disclosure widget which clashes).
 * --------------------------------------------------------------------- */

function PersonaRagAccordion({
  personas,
}: {
  personas: ReadonlyArray<{
    id: string;
    displayName: string;
    expertise?: string;
    role: AgentRole;
    themeColor: string;
  }>;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (personas.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-white/[0.08] px-3 py-4 text-[11px] text-fg-subtle">
        Önce 3. adımda konseyi üret; sonra her üyeye ayrı ayrı belge ekleyebilirsin.
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {personas.map((p) => {
        const isOpen = openId === p.id;
        const subtitle = (p.expertise?.trim() || roleLabel(p.role)).slice(0, 80);
        return (
          <li
            key={p.id}
            className="overflow-hidden rounded-xl border border-white/[0.06] bg-surface-2/30"
          >
            <button
              type="button"
              onClick={() => setOpenId((cur) => (cur === p.id ? null : p.id))}
              aria-expanded={isOpen}
              className="focus-ring flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-white/[0.03]"
            >
              <span
                aria-hidden
                className="h-2.5 w-2.5 flex-none rounded-full"
                style={{ background: p.themeColor }}
              />
              <RoleIcon
                role={p.role}
                className="h-4 w-4 flex-none text-fg-subtle"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-fg-strong">
                  {p.displayName}
                </span>
                <span className="block truncate text-[10px] uppercase tracking-wider text-fg-subtle">
                  {subtitle}
                </span>
              </span>
              <FileText className="h-3.5 w-3.5 flex-none text-fg-subtle" />
              <ChevronDown
                className={cn(
                  "h-4 w-4 flex-none text-fg-subtle transition-transform",
                  isOpen && "rotate-180",
                )}
                aria-hidden
              />
            </button>
            {isOpen ? (
              <div className="border-t border-white/[0.06] px-3 py-3">
                <RagDropZone
                  personaId={p.id}
                  scopeLabel={`Sadece ${p.displayName.split(" ")[0] ?? p.displayName}`}
                  helpText="Bu üyenin RAG bağlamına eklenir, başkası göremez."
                />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * SamplingSlider — purpose-built control for the persona edit drawer's
 * temperature / top_p / max_tokens / penalty knobs.
 *
 * Why a custom component instead of reusing the generic `Slider`:
 *   • Display logic differs — this row shows BOTH the active numeric
 *     value AND a tiny "varsayılan: 0.7" hint that disappears when the
 *     operator overrides the field.
 *   • Per-row "Sıfırla" button clears just THIS knob's override (the
 *     drawer-level "Hepsini sıfırla" wipes the entire object).
 *   • Integer mode (`integer: true`) used for max_tokens so we don't
 *     emit "1280.5".
 *
 * Value resolution order (already done by the caller via
 * `samplingValue`): persona override → catalogue default → undefined.
 * When `undefined` lands here, we display the slider AT the fallback
 * but flagged as "varsayılan" so the operator can move it without
 * losing visual continuity.
 */
function SamplingSlider({
  label,
  hint,
  min,
  max,
  step,
  value,
  fallback,
  onChange,
  integer = false,
}: {
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  value: number | undefined;
  fallback: number | undefined;
  onChange: (next: number | null) => void;
  integer?: boolean;
}) {
  const effective = value ?? fallback ?? min;
  const isOverride = value !== undefined;
  const display = integer
    ? String(Math.round(effective))
    : effective.toFixed(step < 0.1 ? 2 : 1);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium text-fg-strong">{label}</span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              "tabular-nums text-xs",
              isOverride ? "text-accent-200" : "text-fg-subtle",
            )}
          >
            {display}
          </span>
          {isOverride ? (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="focus-ring rounded border border-white/[0.08] px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-fg-subtle hover:border-white/15 hover:text-fg-strong"
              title="Bu alanı modelin varsayılanına döndür"
            >
              sıfırla
            </button>
          ) : null}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={effective}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isFinite(v)) return;
          onChange(integer ? Math.round(v) : v);
        }}
        className="focus-ring h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/[0.06] accent-accent-400"
      />
      <span className="text-[10px] leading-relaxed text-fg-subtle">
        {hint}
        {!isOverride && fallback !== undefined ? (
          <span className="ml-1 opacity-70">
            · varsayılan: {integer ? Math.round(fallback) : fallback.toFixed(2)}
          </span>
        ) : null}
      </span>
    </div>
  );
}


function PersonaEditDrawer({
  open,
  personaId,
  onClose,
}: {
  open: boolean;
  personaId: string | null;
  onClose: () => void;
}) {
  const persona = useDebateStore((s) =>
    s.wizard.personas.find((p) => p.id === personaId),
  );
  const editPersona = useDebateStore((s) => s.editPersona);
  const reduceMotion = useReducedMotion();
  // Live LLM catalogue — fetched once, shared across drawers via the
  // singleton inside `useModelsCatalog`. Renders a "Yükleniyor…" line
  // until it lands; UI degrades gracefully if the endpoint is offline
  // (the operator just can't change the model, save still works).
  const catalog = useModelsCatalog();

  const [draft, setDraft] = useState(persona);

  useEffect(() => {
    setDraft(persona);
  }, [persona]);

  if (!open || !persona || !draft) return null;

  // Taslak, açıldığı andaki personadan sapmış mı? Alan alan karşılaştırmak
  // yerine serileştirip kıyaslıyoruz: çekmece personanın tamamını
  // düzenlenebilir kılıyor ve yeni bir alan eklendiğinde bu kontrolün
  // sessizce eksik kalmasını istemiyoruz.
  const isDirty = JSON.stringify(draft) !== JSON.stringify(persona);

  const onSave = () => {
    const trimmedExpertise = draft.expertise?.trim();
    editPersona(persona.id, {
      displayName: draft.displayName,
      role: draft.role,
      ...(trimmedExpertise ? { expertise: trimmedExpertise } : {}),
      systemInstruction: draft.systemInstruction,
      stanceSummary: draft.stanceSummary,
      // `modelId` is ALWAYS sent (even when undefined) so the slice can
      // distinguish "operator cleared the override" from "field absent
      // in this patch". Empty / whitespace gets normalised to undefined
      // inside `editPersona` → falls back to the process default.
      modelId: draft.modelId ?? "",
      // Sampling overrides — sent verbatim so the slice can detect
      // "cleared" (object with no numeric fields → delete) vs
      // "explicitly set" (object with at least one finite number).
      // DEFENSIVE: backend hydration (e.g. fork → wizard prefill) can
      // leave `samplingOverrides` as null on a freshly-cloned persona;
      // skip the patch in that case so the slice doesn't have to
      // distinguish null-vs-undefined. The slice itself handles null
      // too, but omitting the field is the cleaner contract.
      ...(draft.samplingOverrides && typeof draft.samplingOverrides === "object"
        ? { samplingOverrides: draft.samplingOverrides }
        : {}),
    });
    onClose();
  };

  // Sampling override helpers — bound to the catalogue defaults so the
  // drawer shows the ACTIVE numbers (override > catalogue default >
  // process default) rather than blank inputs the operator has to guess.
  const activeModelId = draft.modelId || catalog.defaultId || null;
  const activeModel = activeModelId
    ? catalog.models.find((m) => m.id === activeModelId)
    : undefined;
  const modelDefaults = activeModel?.defaults;
  // Operator-facing default-model label — never leak the internal slug
  // (`brain-31b`, `coder-qwen` …) into the dropdown placeholder. We
  // resolve the catalogue label of the global default and fall back to
  // a sterile "DAI Konsey" string only if the catalogue hasn't loaded.
  const defaultModelLabel = catalog.defaultId
    ? catalog.models.find((m) => m.id === catalog.defaultId)?.label ?? null
    : null;

  const setSampling = (
    field: keyof NonNullable<typeof draft.samplingOverrides>,
    value: number | null,
  ) => {
    setDraft((d) => {
      if (!d) return d;
      const cur = d.samplingOverrides ?? {};
      const next = { ...cur } as typeof cur;
      if (value === null || !Number.isFinite(value)) {
        delete (next as Record<string, unknown>)[field];
      } else {
        (next as Record<string, number>)[field] = value;
      }
      if (Object.keys(next).length === 0) {
        const { samplingOverrides: _drop, ...rest } = d;
        void _drop;
        return rest as typeof d;
      }
      return { ...d, samplingOverrides: next };
    });
  };

  const samplingValue = (
    field: keyof NonNullable<typeof draft.samplingOverrides>,
    fallback: number | undefined,
  ): number | undefined => {
    const ov = draft.samplingOverrides?.[field];
    if (typeof ov === "number" && Number.isFinite(ov)) return ov;
    return fallback;
  };

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        // Kaydedilmemiş düzenleme varken boşluğa tıklamak taslağı sessizce
        // siliyordu: `draft` yalnızca bu çekmecenin yerel state'inde durur ve
        // kapanışta unmount ile gider. Değişiklik yapılmışsa kazara kapanmayı
        // engelliyoruz — vazgeçmek için "İptal" düğmesi zaten var.
        onClick={() => {
          if (isDirty) {
            toast.message("Kaydedilmemiş değişiklik var", {
              description:
                "Kaydet ya da İptal ile çık — boşluğa tıklamak "
                + "düzenlemeni silmesin diye kapatmadık.",
            });
            return;
          }
          onClose();
        }}
      />
      <motion.aside
        key="drawer"
        initial={reduceMotion ? false : { x: "100%" }}
        animate={{ x: 0 }}
        exit={reduceMotion ? { opacity: 0 } : { x: "100%" }}
        transition={{ type: "spring", stiffness: 240, damping: 28 }}
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-white/[0.06] bg-surface-1/95 shadow-2xl backdrop-blur-xl"
        role="dialog"
        aria-label={`Düzenle: ${persona.displayName}`}
      >
        <header className="sticky top-0 z-10 flex items-start justify-between border-b border-white/[0.06] bg-surface-1/95 px-5 py-4 backdrop-blur-xl">
          <div>
            <h3 className="text-base font-semibold text-fg-strong">
              Konsey üyesini düzenle
            </h3>
            <p className="text-xs text-fg-subtle">
              Bu değişiklikler yalnız bu sezona aittir.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="focus-ring rounded-md px-2 py-1 text-fg-subtle hover:bg-white/[0.05] hover:text-fg-strong"
            aria-label="Kapat"
          >
            Kapat
          </button>
        </header>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-fg-strong">İsim</span>
            <input
              type="text"
              value={draft.displayName}
              onChange={(e) =>
                setDraft({ ...draft, displayName: e.target.value })
              }
              className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-fg-strong">Meslek unvanı</span>
            <input
              type="text"
              value={draft.expertise ?? ""}
              maxLength={80}
              placeholder="örn. C4ISR Mimarı, EW Uzmanı, Aviyonik Baş Mühendisi"
              onChange={(e) =>
                setDraft({ ...draft, expertise: e.target.value })
              }
              className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong"
            />
            <span className="text-[10px] text-fg-subtle">
              Soruya doğrudan katkı sağlayan, sektörde gerçekten var olan
              meslek unvanı.
            </span>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-fg-strong">
              Bilişsel duruş (cognitive disposition)
            </span>
            <select
              value={draft.role}
              onChange={(e) =>
                setDraft({ ...draft, role: e.target.value as AgentRole })
              }
              className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-fg-subtle">
              UI ikonu ve tartışma tavrını belirler. Uzmanlığı `expertise`
              alanı taşır.
            </span>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-fg-strong">Stance özeti</span>
            <input
              type="text"
              value={draft.stanceSummary}
              maxLength={140}
              onChange={(e) =>
                setDraft({ ...draft, stanceSummary: e.target.value })
              }
              className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong"
            />
          </label>
          {/* Per-persona LLM model picker.
              Pulls the live catalogue from `/api/debate/models`. The
              dropdown is split visually by `family` (primary vs fast vs
              other) so the operator immediately sees which model is the
              flagship vs the lightweight one. The first option clears
              any override (process default takes over). */}
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-fg-strong">LLM modeli</span>
            {catalog.status === "loading" ? (
              <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-xs text-fg-subtle">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Model kataloğu yükleniyor…
              </div>
            ) : catalog.status === "error" ? (
              <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                Katalog alınamadı: {catalog.error ?? "bilinmeyen hata"}
              </div>
            ) : (
              <>
                <select
                  value={draft.modelId ?? ""}
                  onChange={(e) => {
                    // exactOptionalPropertyTypes: distinguish "absent"
                    // from "undefined" — clearing the dropdown must
                    // literally delete the field, not set it to undefined.
                    const v = e.target.value;
                    setDraft((d) => {
                      if (!d) return d;
                      if (v) return { ...d, modelId: v };
                      const { modelId: _drop, ...rest } = d;
                      void _drop;
                      return rest as typeof d;
                    });
                  }}
                  className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong"
                >
                  <option value="">
                    Varsayılan model
                    {defaultModelLabel ? ` (${defaultModelLabel})` : ""}
                  </option>
                  {/* Group by performance tier (`fast` / `balanced` /
                      `deep`). Backend emits both `tier` and the legacy
                      `speedTier` alias for older bundles; we read the
                      first available so neither side blocks rollout. */}
                  {(["deep", "balanced", "fast", "other"] as const).flatMap(
                    (tier) => {
                      const group = catalog.models.filter((m) => {
                        const t = (m.tier ?? m.speedTier) ?? "other";
                        return t === tier;
                      });
                      if (group.length === 0) return [];
                      const label =
                        tier === "deep"
                          ? "Derin Muhakeme (flagship)"
                          : tier === "balanced"
                            ? "Dengeli (genel kullanım)"
                            : tier === "fast"
                              ? "Hızlı / Düşük Gecikme"
                              : "Diğer";
                      return [
                        <optgroup key={tier} label={label}>
                          {group.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.label}
                              {m.contextLen
                                ? ` · ${Math.round(m.contextLen / 1024)}K ctx`
                                : ""}
                            </option>
                          ))}
                        </optgroup>,
                      ];
                    },
                  )}
                </select>
                {activeModel?.description ? (
                  <span className="rounded-md border border-white/[0.06] bg-surface-2/40 px-2 py-1.5 text-[10px] leading-relaxed text-fg-muted">
                    {activeModel.description}
                  </span>
                ) : null}
              </>
            )}
            <span className="text-[10px] text-fg-subtle">
              Bu üye konuştuğunda kullanılacak model. Boş bırakılırsa
              orkestratörün varsayılan rotası devreye girer.
            </span>
          </label>

          {/* ─────────────────────────────────────────────────────────
              Sampling Overrides — temperature / top_p / max_tokens /
              presence / frequency penalty. Each control shows the
              ACTIVE value (override → catalogue default → undefined).
              Reset button per-row clears the override only.
              ───────────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-3 rounded-xl border border-white/[0.06] bg-surface-2/40 p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-fg-strong">
                  Örnekleme parametreleri
                </div>
                <div className="text-[11px] leading-relaxed text-fg-subtle">
                  Bu üye için sıcaklık, top-p, token limiti ve cezaları
                  ayarla. Boş bırakılan alanlar modelin varsayılanına
                  düşer.
                </div>
              </div>
              {draft.samplingOverrides ? (
                <button
                  type="button"
                  onClick={() => {
                    setDraft((d) => {
                      if (!d) return d;
                      const { samplingOverrides: _drop, ...rest } = d;
                      void _drop;
                      return rest as typeof d;
                    });
                  }}
                  className="focus-ring rounded-md border border-white/[0.08] px-2 py-1 text-[10px] uppercase tracking-wider text-fg-subtle hover:border-white/15 hover:text-fg-strong"
                >
                  Hepsini sıfırla
                </button>
              ) : null}
            </div>

            <SamplingSlider
              label="Sıcaklık (temperature)"
              hint="0.0 deterministik · 1.0 dengeli · 1.5+ yaratıcı"
              min={0.0}
              max={1.5}
              step={0.05}
              value={samplingValue("temperature", modelDefaults?.temperature)}
              fallback={modelDefaults?.temperature}
              onChange={(v) => setSampling("temperature", v)}
            />

            <SamplingSlider
              label="Top-p (nucleus)"
              hint="Olasılık kütlesi eşiği — düşürüp model'i sıkı tutabilirsin"
              min={0.1}
              max={1.0}
              step={0.02}
              value={samplingValue("topP", modelDefaults?.topP)}
              fallback={modelDefaults?.topP}
              onChange={(v) => setSampling("topP", v)}
            />

            <SamplingSlider
              label="Maksimum token"
              hint="Bu üyenin tek bir turda üretebileceği üst sınır"
              min={256}
              max={4096}
              step={64}
              value={samplingValue("maxTokens", modelDefaults?.maxTokens)}
              fallback={modelDefaults?.maxTokens}
              onChange={(v) => setSampling("maxTokens", v)}
              integer
            />

            <SamplingSlider
              label="Presence penalty"
              hint="Pozitif değer yeni konulara açılır; negatif tekrara yatkın"
              min={-2.0}
              max={2.0}
              step={0.1}
              value={samplingValue(
                "presencePenalty",
                modelDefaults?.presencePenalty,
              )}
              fallback={modelDefaults?.presencePenalty}
              onChange={(v) => setSampling("presencePenalty", v)}
            />

            <SamplingSlider
              label="Frequency penalty"
              hint="Pozitif değer aynı kelimeyi tekrar etmeye karşı bastırır"
              min={-2.0}
              max={2.0}
              step={0.1}
              value={samplingValue(
                "frequencyPenalty",
                modelDefaults?.frequencyPenalty,
              )}
              fallback={modelDefaults?.frequencyPenalty}
              onChange={(v) => setSampling("frequencyPenalty", v)}
            />
          </div>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-fg-strong">Sistem yönergesi</span>
            <textarea
              value={draft.systemInstruction}
              rows={9}
              onChange={(e) =>
                setDraft({ ...draft, systemInstruction: e.target.value })
              }
              className="focus-ring min-h-[10rem] resize-y rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 font-mono text-xs text-fg-strong"
            />
          </label>

          {/* Persona-private knowledge base.
              `personaId={persona.id}` ⇒ documents are scoped to this seat
              alone; the engine's RAG retriever filters on `persona_id` so
              other speakers cannot see them. Useful when one expert needs
              a confidential brief (e.g. "Hukuk Danışmanı"nın görüşünü
              dayandıracağı sözleşme metni) the rest of the council
              shouldn't be biased by. */}
          <div className="flex flex-col gap-2 rounded-xl border border-white/[0.06] bg-surface-2/40 p-3">
            <div>
              <div className="text-sm font-semibold text-fg-strong">
                Bu üyenin belgeleri
              </div>
              <div className="text-[11px] leading-relaxed text-fg-subtle">
                Yalnız <strong className="text-fg-muted">{persona.displayName}</strong>{" "}
                tarafından RAG olarak kullanılır. Tüm konseyin görmesi
                gerekenleri ortak havuza yükle (4. adım).
              </div>
            </div>
            <RagDropZone
              personaId={persona.id}
              scopeLabel="Üyeye özel belge bırak"
              helpText="Sadece bu uzmanın bağlamına eklenir."
            />
          </div>
        </div>
        <footer className="sticky bottom-0 z-10 flex items-center justify-end gap-2 border-t border-white/[0.06] bg-surface-1/95 px-5 py-3 backdrop-blur-xl">
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={onSave}>
            <Check className="h-4 w-4" /> Kaydet
          </Button>
        </footer>
      </motion.aside>
    </AnimatePresence>
  );
}

/* --------------------------------------------------------------------- *
 * Main wizard
 * --------------------------------------------------------------------- */

export function SetupWizard() {
  const wizard = useDebateStore((s) => s.wizard);
  const stagedDocCount = useDebateStore((s) => s.stagedDocCount);
  const setStep = useDebateStore((s) => s.setStep);
  const setQuery = useDebateStore((s) => s.setQuery);
  const setAgentCount = useDebateStore((s) => s.setAgentCount);
  const setMaxRounds = useDebateStore((s) => s.setMaxRounds);
  const setAutoFinalize = useDebateStore((s) => s.setAutoFinalize);
  const setCritiqueStrictness = useDebateStore((s) => s.setCritiqueStrictness);
  const setTerminationProfile = useDebateStore((s) => s.setTerminationProfile);
  const setPrivateMode = useDebateStore((s) => s.setPrivateMode);
  const setUrgency = useDebateStore((s) => s.setUrgency);
  const setConstraints = useDebateStore((s) => s.setConstraints);
  const setUserDebatePrompt = useDebateStore((s) => s.setUserDebatePrompt);
  const assessQuestion = useDebateStore((s) => s.assessQuestion);
  const generatePersonas = useDebateStore((s) => s.generatePersonas);
  const regeneratePersona = useDebateStore((s) => s.regeneratePersona);
  const removePersona = useDebateStore((s) => s.removePersona);
  const status = useDebateStore((s) => s.status);
  const closeWizard = useDebateStore((s) => s.closeWizard);
  const debate = useStartDebate();
  const reduceMotion = useReducedMotion();

  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [busyPersonaId, setBusyPersonaId] = useState<string | null>(null);
  const stepTransitionRef = useRef(false);

  const isLocked = status === "running" || status === "provisioning";

  const isStepReachable = useCallback(
    (target: WizardStep): boolean => {
      if (target <= wizard.step) return true;
      // Allow forward jumps only if all earlier steps are valid.
      for (let s = 1; s < target; s = (s + 1) as WizardStep) {
        if (!isStepValid(s as WizardStep, wizard)) return false;
      }
      return true;
    },
    [wizard],
  );

  const guardedSetStep = useCallback(
    (next: WizardStep) => {
      if (stepTransitionRef.current) return;
      stepTransitionRef.current = true;
      setStep(next);
      requestAnimationFrame(() => {
        stepTransitionRef.current = false;
      });
    },
    [setStep],
  );

  // Soru kalite kapısı — adım 1'den çıkışta bir kez ölçüm yapar.
  // Bloklamaz: ölçüm sonucu ekranda gösterilir, ikinci tık geçirir.
  // Dönüş değeri "geçiş yapıldı mı" bilgisidir; klavye kısayolu da
  // aynı yolu kullanır, iki farklı davranış oluşmasın diye.
  const advanceFromQuery = useCallback(async (): Promise<boolean> => {
    if (isQualityGateCleared(wizard)) return true;
    const q = wizard.quality;
    if (q.status === "assessing" || q.status === "refining") return false;
    if (!isQualityReportFresh(q, wizard.query)) {
      await assessQuestion();
      return false;
    }
    toast.message(t("setup.quality.blockedToast"));
    return false;
  }, [wizard, assessQuestion]);

  const goNext = useCallback(async () => {
    if (!isStepValid(wizard.step, wizard)) return;
    if (wizard.step === 1 && !(await advanceFromQuery())) return;
    if (wizard.step < 4) guardedSetStep((wizard.step + 1) as WizardStep);
  }, [wizard, guardedSetStep, advanceFromQuery]);

  const goBack = useCallback(() => {
    if (wizard.step > 1) guardedSetStep((wizard.step - 1) as WizardStep);
  }, [wizard.step, guardedSetStep]);

  const onComposerShortcut = useCallback(
    (seed: QueryComposerSubmitSeed) => {
      if (wizard.step === 1 && isStepValid(1, wizard)) {
        void advanceFromQuery().then((ok) => {
          if (ok) guardedSetStep(2);
        });
        return;
      }
      if (wizard.step === 4 && isStepValid(4, wizard)) {
        void debate
          .start({ origin: "keydown", isTrusted: seed.isTrusted, timeStamp: seed.timeStamp })
          .catch((err: unknown) => {
            console.error("[wizard] keyboard-start failed:", err);
          });
      }
    },
    [wizard, guardedSetStep, debate, advanceFromQuery],
  );

  const onGenerate = useCallback(async () => {
    await generatePersonas();
    if (useDebateStore.getState().wizard.generationStatus === "error") {
      const err = useDebateStore.getState().wizard.generationError;
      toast.error(err ?? "Konsey üretici yanıt vermedi.");
      return;
    }
    toast.success(`Konsey hazır — ${useDebateStore.getState().wizard.personas.length} üye.`);
  }, [generatePersonas]);

  // Auto-trigger persona generation when the user lands on step 3 with a
  // valid query. Re-runs whenever the trigger conditions change so we
  // recover gracefully from the half-states that previously left the
  // panel empty:
  //   • generationStatus="error" carried over from a prior session
  //   • personas array cleared mid-flight (manual remove)
  //   • query edited on Step 1 → user comes back to Step 3
  //
  // The narrow [wizard.step] dependency in the previous version meant
  // the effect only fired ONCE on the step transition; if the very
  // first attempt errored, the screen sat blank until the operator
  // hand-clicked "Konseyi Üret" — which they often never noticed
  // because the empty-state copy lived under the fold.
  useEffect(() => {
    if (wizard.step !== 3) return;
    // Persona Kütüphanesi — otomatik üretim ancak kullanıcı bunu AÇIKÇA
    // seçtiğinde çalışır. Eskiden adım 3'e girer girmez tetikleniyordu ve
    // kullanıcı kütüphanesindeki hazır takımı/birimi seçme fırsatı bulamadan
    // hem konsey hem LLM maliyeti oluşuyordu (kullanıcı bildirimi).
    if (wizard.councilSource !== "auto") return;
    const personas = Array.isArray(wizard.personas) ? wizard.personas : [];
    if (personas.length > 0) return;
    if (wizard.generationStatus === "generating" || wizard.generationStatus === "error") return;
    if (wizard.query.trim().length < WIZARD_LIMITS.queryMin) return;
    if (isLocked) return;
    // Defensive: a corrupted agentCount (NaN / 0) would make the
    // backend reject with `agent_count must be ≥ 3` and leave the
    // wizard frozen in a blank "generating" state. Heal locally.
    if (
      !Number.isFinite(wizard.agentCount) ||
      wizard.agentCount < WIZARD_LIMITS.agentCountMin
    ) {
      setAgentCount(WIZARD_DEFAULTS.agentCount);
    }
    void generatePersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wizard.step,
    wizard.personas?.length ?? 0,
    wizard.generationStatus,
    wizard.query,
    wizard.councilSource,
    isLocked,
  ]);

  const onRegenerateOne = useCallback(
    async (id: string) => {
      setBusyPersonaId(id);
      try {
        await regeneratePersona(id);
      } finally {
        setBusyPersonaId(null);
      }
    },
    [regeneratePersona],
  );

  const onStart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const gesture = {
        origin: "click" as const,
        isTrusted: e.nativeEvent.isTrusted,
        timeStamp: e.nativeEvent.timeStamp,
      };

      // Transient 502/503 on SSE start — retry up to 3 times with backoff
      // before surfacing error to user. The wizard stays open during retries.
      const MAX_START_RETRIES = 3;
      const START_RETRY_BASE_MS = 2_000;

      const attemptStart = async (attemptsLeft: number): Promise<void> => {
        try {
          await debate.start(gesture);
          // Görev 5 — a needs_clarification 409 resolves `start()`
          // WITHOUT throwing (see sessionSlice's catch block) so the
          // clarification prompt can be shown without the caller
          // treating it as a hard error. Only close the wizard when
          // no prompt is pending — otherwise `ClarificationModal`
          // (mounted alongside the wizard) needs the wizard to stay
          // open so the operator can answer and resubmit in place.
          if (!useDebateStore.getState().clarification) {
            closeWizard();
          }
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          const isTransient =
            msg.includes("503") || msg.includes("502") || msg.includes("504") ||
            msg.includes("network") || msg.includes("ERR_");

          if (isTransient && attemptsLeft > 0) {
            const attempt = MAX_START_RETRIES - attemptsLeft + 1;
            const delay = START_RETRY_BASE_MS * Math.pow(2, attempt - 1);
            toast.warning(
              `Sunucu geçici olarak meşgul — ${attempt}. deneme (${delay / 1000}s sonra)`,
              { id: "start-retry", duration: delay + 500 },
            );
            await new Promise((r) => setTimeout(r, delay));
            return attemptStart(attemptsLeft - 1);
          }

          console.error("[wizard] start failed:", err);
          toast.error(
            isTransient
              ? "Sunucuya bağlanılamadı. Birkaç saniye bekleyip tekrar deneyin."
              : "Tartışma başlatılamadı. Konsoldaki hatayı kontrol et.",
          );
        }
      };

      void attemptStart(MAX_START_RETRIES);
    },
    [debate, closeWizard],
  );

  // Adım 4 özeti ve başlat düğmesi ARTIK doğrudan `wizard.personas` okumaz —
  // kütüphane yolunda o dizi kasten boştur (bkz. `useResolvedCouncil`).
  const council = useResolvedCouncil();
  // Maliyet tahmini gerçekleşen üye sayısına göre; `agentCount` yalnız
  // otomatik üretimin HEDEFİdir, kütüphane yolunda hiçbir anlamı yok.
  const cost = useMemo(
    () =>
      estimateDebateCost({
        ...wizard,
        agentCount: council.count > 0 ? council.count : wizard.agentCount,
      }),
    [wizard, council.count],
  );

  return (
    <Card glow tone="elevated" className="relative">
      <StepIndicator
        current={wizard.step}
        jumpTo={(s) => {
          if (isStepReachable(s)) guardedSetStep(s);
        }}
        isStepReachable={isStepReachable}
      />

      <CardHeader
        icon={<Wand2 className="h-4 w-4" aria-hidden />}
        title={STEP_TITLES[wizard.step].label}
        subtitle={STEP_TITLES[wizard.step].subtitle}
        className="border-b-0 pb-0"
        actions={
          <button
            type="button"
            onClick={async () => {
              // Quick-start: ensure query, generate personas if absent,
              // THEN jump to the preview step. The previous version skipped
              // persona generation, which left the launch button perma-
              // disabled — the classic "Hızlı başlat → bir bok olmadı"
              // symptom users hit before they realised they had to step
              // through the wizard manually.
              if (!wizard.query?.trim()) {
                toast.error("Lütfen önce bir tartışma sorusu girin.");
                return;
              }
              setQuery(wizard.query);
              const haveCouncil = useDebateStore.getState().wizard.personas.length > 0;
              if (!haveCouncil) {
                const t = toast.loading("Hızlı başlat: konsey üretiliyor…");
                try {
                  await generatePersonas();
                  const errStatus =
                    useDebateStore.getState().wizard.generationStatus;
                  if (errStatus === "error") {
                    toast.error(
                      useDebateStore.getState().wizard.generationError ??
                        "Konsey üretici yanıt vermedi.",
                      { id: t },
                    );
                    return;
                  }
                  toast.success("Konsey hazır.", { id: t });
                } catch (e) {
                  toast.error(`Konsey üretilemedi: ${(e as Error).message}`, {
                    id: t,
                  });
                  return;
                }
              }
              guardedSetStep(4);
              toast.message("Hızlı başlat: 4. adımdasın, başlatabilirsin.");
            }}
            disabled={isLocked}
            className="focus-ring rounded-full border border-white/[0.08] bg-surface-2/60 px-3 py-1 text-[11px] uppercase tracking-wider text-fg-subtle hover:border-white/15 hover:text-fg-strong"
          >
            Hızlı başlat
          </button>
        }
      />

      <div className="flex flex-col gap-4 p-4 sm:p-5">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={wizard.step}
            layout
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="flex flex-col gap-4"
          >
            {/* ADIM 1 = TEK İŞ: soruyu netleştirmek.
                `QueryComposer` artık kalite kapısını kendi gövdesinde
                barındırır (tek panel). Bağlam belgeleri buradan 2. adıma
                taşındı: belge yüklemek soruyu netleştirmenin parçası
                değildir ve operatörün odağını bölüyordu. */}
            {wizard.step === 1 ? (
              <QueryComposer onSubmitShortcut={onComposerShortcut} />
            ) : null}

            {wizard.step === 2 ? (
              <div className="flex flex-col gap-4">
                {/* Bağlam Belgeleri — konu özel bir proje/kurum içeriyorsa
                    brifing dökümanları burada yüklenir. Aynı `RagDropZone`
                    4. adımdaki "Ortak Bilgi Tabanı" ile pool scope'unu
                    PAYLAŞIR — sessionId aynıdır. Yüklenen belgeler (a)
                    konsey üretilirken persona designer'a bağlam olarak
                    verilir, (b) tartışma sırasında her konuşmacı
                    tarafından RAG ile aranır. Bu yüzden konsey
                    yapılandırmasının ÜSTÜNDE durur. */}
                <section className="glass rounded-xl border border-white/[0.06] p-4">
                  <header className="mb-3 flex items-start gap-3">
                    <div
                      aria-hidden
                      className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-accent-400/30 bg-accent-500/10 text-accent-200"
                    >
                      <FileText className="h-4 w-4" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-fg-strong">
                        Bağlam Belgeleri
                        <span className="ml-2 rounded-full border border-white/10 bg-surface-2/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
                          opsiyonel
                        </span>
                      </h4>
                      <p className="mt-0.5 text-[11px] leading-relaxed text-fg-subtle">
                        Sorduğun konu özel bir proje, kurum ya da
                        kısaltma içeriyorsa (UKKS, ATAK, MİLGEM, kurumsal
                        bir mimari belge, vs.) brifing dökümanlarını
                        burada yükle. <strong className="text-fg-muted">
                          Konsey üyeleri bu belgelerden domain-aware
                          uzmanlar olarak tasarlanır
                        </strong>{" "}
                        ve tartışma sırasında her uzman bu belgeleri
                        RAG ile sorgular.
                      </p>
                    </div>
                  </header>
                  <RagDropZone
                    scopeLabel="Bağlam belgesi bırak ya da seç"
                    helpText="Konsey üretiminden ÖNCE etkili olur. Taranmış PDF'ler otomatik OCR'dan geçer."
                  />
                </section>
                <TopologyPicker />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Slider
                    label="Konsey Boyutu"
                    value={wizard.agentCount}
                    min={WIZARD_LIMITS.agentCountMin}
                    max={WIZARD_LIMITS.agentCountMax}
                    step={1}
                    onChange={setAgentCount}
                    marks={[3, 5, 8, 12, 16, 20]}
                    hint={`Moderatör + uzmanlar — 5 önerilir, ${WIZARD_LIMITS.agentCountMax}'ye kadar destekli.`}
                    formatValue={(v) => `${v} üye`}
                  />
                  <Slider
                    label="Maksimum Tur"
                    value={wizard.maxRounds}
                    min={WIZARD_LIMITS.maxRoundsMin}
                    max={WIZARD_LIMITS.maxRoundsMax}
                    step={1}
                    onChange={setMaxRounds}
                    marks={[2, 5, 10, 20, 30, 40, 50]}
                    hint={`Tartışma sert biterse otomatik kısalır — ${WIZARD_LIMITS.maxRoundsMax} tura kadar destekli.`}
                    formatValue={(v) => `${v} tur`}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {/*
                    Konsey Cognitive Renaissance — Bölüm A6.
                    "Otomatik bitirme" toggle = `auto_finalize`. Off
                    olunca konsensüs/deadlock erken-çıkış kapatılır,
                    sadece tur bütçesi tükenince biter — kullanıcının
                    "20 tur garanti olsun" isterinin en sıkı yanıtı.
                    Etiket netleştirildi: "Erken sonlanmaya izin ver".
                  */}
                  <Switch
                    checked={wizard.autoFinalize}
                    onChange={setAutoFinalize}
                    label="Erken sonlanmaya izin ver"
                    hint="Kapalıysa: tartışma TAMAMLANINCAYA kadar sürer. Açıksa: konsensüs/deadlock'ta erken biter."
                  />
                  <SegmentedControl<CritiqueStrictness>
                    label="Kritik Sıkılığı"
                    value={wizard.critiqueStrictness}
                    onChange={setCritiqueStrictness}
                    options={STRICTNESS_OPTIONS}
                    tooltip={
                      <span className="block space-y-1.5 leading-relaxed">
                        <span className="block">
                          Her üye söz almadan önce, kendinden önceki
                          argümanları hedef alan bir <b>kritik</b> bloğu
                          üretmek zorundadır. Bu ayar, kritik geçersiz
                          sayıldığında modele kaç kez yeniden şans
                          verileceğini belirler.
                        </span>
                        <span className="block">
                          Haklar tükenirse üye o turda <b>söz hakkını
                          kaybeder</b> ve tartışma bir sonraki konuşmacıya
                          geçer.
                        </span>
                        <span className="block text-fg-muted">
                          <b>Sert</b> (1 deneme): en yüksek kalite çıtası,
                          daha çok söz kaybı. <b>Esnek</b> (3 deneme):
                          toleranslı, daha fazla konuşma ama daha zayıf
                          eleştiriler.
                        </span>
                      </span>
                    }
                  />
                </div>
                {/*
                 * Konsey Cognitive Renaissance — Bölüm A0/A1.
                 * 3-fazlı erken sonlanma profili. Kullanıcı 20 tur seçtiğinde
                 * sıkı profil seçerse en az 15 tur garanti edilir; "konsey 6
                 * turda elsıkıştı" şikayeti sıfırlanır.
                 */}
                <SegmentedControl<TerminationProfile>
                  label="Erken Sonlanma Profili"
                  value={wizard.terminationProfile}
                  onChange={setTerminationProfile}
                  options={TERMINATION_PROFILE_OPTIONS}
                />
                <p className="text-[11px] leading-relaxed text-fg-subtle">
                  {wizard.terminationProfile === "loose"
                    ? "Konsey hızlıca anlaşırsa erken sonlanır. Pilot ve hızlı brainstorm için."
                    : wizard.terminationProfile === "strict"
                    ? "Garanti tartışma. Konsey ancak çok güçlü konsensüs veya çok belirgin deadlock'ta erken kapanır. Aşırı uyum tespitinde otomatik Devil's Advocate çağrılır."
                    : "Dengeli — gerçek konsensüs sağlanmadan ve yeterli tartışma olmadan kapanmaz. Tavsiye edilir."}
                </p>

                {/*
                 * Konsey Yönergesi — soru "ne sorulduğunu", yönerge
                 * "hangi eksende çalışılacağını" belirler. Backend'de
                 * her personanın sistem prompt'una EN ÜSTTE girer.
                 */}
                <section className="glass rounded-xl border border-white/[0.06] p-4">
                  <header className="mb-3">
                    <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-fg-muted">
                      <Compass className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
                      Konsey Yönergesi
                    </h4>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-fg-subtle">
                      Konseyin bu soruyu hangi eksende işleyeceğini yazın:
                      neye odaklansın, nereye derinleşmesin, sonuçta hangi
                      somut çıktıyı üretsin. Soruyla çelişirse yönerge
                      geçerlidir.
                    </p>
                  </header>
                  <div className="mb-2.5 flex flex-wrap gap-1.5">
                    {DIRECTIVE_PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => {
                          const current = wizard.userDebatePrompt?.trim() ?? "";
                          setUserDebatePrompt(
                            current.length > 0
                              ? `${current}\n\n${preset.text}`
                              : preset.text,
                          );
                        }}
                        className="focus-ring rounded-full border border-white/10 bg-surface-0/60 px-2.5 py-1 text-[11px] font-medium text-fg-muted transition hover:border-accent/40 hover:text-fg-strong"
                      >
                        + {preset.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={wizard.userDebatePrompt ?? ""}
                    onChange={(e) => setUserDebatePrompt(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    placeholder="örn. Tartışmanın omurgası yüklenen finansal tablolar olsun; son 3 yılın satış seyrini ve ürün portföyünü tartışın, donanım mimarisine inmeyin. Sonuçta net bir yatırım tavsiyesi ve onu bozacak eşikler isterim."
                    className="focus-ring w-full resize-y rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-xs leading-relaxed text-fg-strong placeholder:text-fg-subtle"
                  />
                  <p className="mt-1 text-right text-[10px] tabular-nums text-fg-subtle">
                    {(wizard.userDebatePrompt ?? "").length} / 4000
                  </p>
                </section>

                {/*
                 * Görev 3-5 — Karar Bağlamı: gizli mod, aciliyet ve
                 * yapılandırılmış kısıtlar. Doldurulan kısıt alanları
                 * backend'in netleştirme sorusu sormasını bastırır
                 * (bkz. decision_framing.py::_clarifying).
                 */}
                <section className="glass rounded-xl border border-white/[0.06] p-4">
                  <header className="mb-3">
                    <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-fg-muted">
                      <Lock className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
                      Karar Bağlamı
                    </h4>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-fg-subtle">
                      Bu alanlar doldurulursa konsey netleştirici soru
                      sormadan doğrudan tartışmaya başlar.
                    </p>
                  </header>
                  <div className="flex flex-col gap-4">
                    <Switch
                      checked={wizard.privateMode}
                      onChange={setPrivateMode}
                      label="Gizli Mod"
                      hint="Açıksa hiçbir sorgu genel web arama backend'lerine gitmez — yalnız özel korpus/RAG kullanılır."
                    />
                    <SegmentedControl<UrgencyOption>
                      label="Aciliyet"
                      value={wizard.urgency ?? "auto"}
                      onChange={(v) => setUrgency(v === "auto" ? null : v)}
                      options={URGENCY_OPTIONS}
                    />
                    <div className="grid gap-3 sm:grid-cols-3">
                      <label className="flex flex-col gap-1.5 text-xs">
                        <span className="font-medium text-fg-strong">
                          Bütçe
                        </span>
                        <input
                          type="text"
                          value={wizard.constraints.budget ?? ""}
                          onChange={(e) =>
                            setConstraints({ budget: e.target.value })
                          }
                          placeholder="örn. 5M USD üst sınır"
                          maxLength={200}
                          className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong placeholder:text-fg-subtle"
                        />
                      </label>
                      <label className="flex flex-col gap-1.5 text-xs">
                        <span className="font-medium text-fg-strong">
                          Süre / Takvim
                        </span>
                        <input
                          type="text"
                          value={wizard.constraints.timeline ?? ""}
                          onChange={(e) =>
                            setConstraints({ timeline: e.target.value })
                          }
                          placeholder="örn. 6 ay içinde canlıya alım"
                          maxLength={200}
                          className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong placeholder:text-fg-subtle"
                        />
                      </label>
                      <label className="flex flex-col gap-1.5 text-xs">
                        <span className="font-medium text-fg-strong">
                          Risk Toleransı
                        </span>
                        <input
                          type="text"
                          value={wizard.constraints.riskTolerance ?? ""}
                          onChange={(e) =>
                            setConstraints({ riskTolerance: e.target.value })
                          }
                          placeholder="örn. düşük — kanıtlanmış çözüm tercih"
                          maxLength={200}
                          className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong placeholder:text-fg-subtle"
                        />
                      </label>
                    </div>
                  </div>
                </section>
              </div>
            ) : null}

            {wizard.step === 3 ? (
              <Step3Body
                wizard={wizard}
                isLocked={isLocked}
                busyPersonaId={busyPersonaId}
                onGenerate={onGenerate}
                onRegenerateOne={onRegenerateOne}
                onRemove={removePersona}
                onEditOpen={setDrawerId}
                onJumpToQuery={() => guardedSetStep(1)}
              />
            ) : null}

            {wizard.step === 4 ? (
              <div className="flex flex-col gap-4">
                <section className="glass rounded-xl border border-white/[0.06] p-4">
                  <header className="mb-2 flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                      Stratejik Soru
                    </h4>
                    <button
                      type="button"
                      onClick={() => guardedSetStep(1)}
                      className="text-xs text-accent-300 hover:underline"
                    >
                      Düzenle
                    </button>
                  </header>
                  <p className="text-sm leading-relaxed text-fg-strong">
                    {wizard.query || "—"}
                  </p>
                </section>

                <section className="grid gap-3 sm:grid-cols-2">
                  <div className="glass rounded-xl border border-white/[0.06] p-4">
                    <header className="mb-2 flex items-center justify-between">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                        Yapılandırma
                      </h4>
                      <button
                        type="button"
                        onClick={() => guardedSetStep(2)}
                        className="text-xs text-accent-300 hover:underline"
                      >
                        Düzenle
                      </button>
                    </header>
                    <ul className="space-y-1.5 text-xs text-fg-muted">
                      <li>
                        <span className="text-fg-subtle">Topoloji:</span>{" "}
                        <span className="text-fg-strong">
                          {i18n.setup.topology[wizard.topology]?.label ?? wizard.topology}
                        </span>
                      </li>
                      <li>
                        <span className="text-fg-subtle">Boyut:</span>{" "}
                        <span className="text-fg-strong">
                          {council.count > 0 ? council.count : wizard.agentCount} üye
                        </span>
                        {council.isLibrary && (
                          <span className="ml-1 text-[10px] text-fg-subtle">
                            (kütüphaneden)
                          </span>
                        )}
                      </li>
                      <li>
                        <span className="text-fg-subtle">Maks. tur:</span>{" "}
                        <span className="text-fg-strong">{wizard.maxRounds}</span>
                      </li>
                      <li>
                        <span className="text-fg-subtle">Otomatik bitirme:</span>{" "}
                        <span className="text-fg-strong">
                          {wizard.autoFinalize ? "açık" : "kapalı"}
                        </span>
                      </li>
                      <li>
                        <span className="text-fg-subtle">Sıkılık:</span>{" "}
                        <span className="text-fg-strong">
                          {wizard.critiqueStrictness}
                        </span>
                      </li>
                      <li>
                        <span className="text-fg-subtle">
                          Erken sonlanma:
                        </span>{" "}
                        <span className="text-fg-strong">
                          {wizard.terminationProfile === "loose"
                            ? "Basit"
                            : wizard.terminationProfile === "strict"
                            ? "Sıkı"
                            : "Orta"}
                        </span>
                      </li>
                      <li>
                        <span className="text-fg-subtle">Gizli mod:</span>{" "}
                        <span className="text-fg-strong">
                          {wizard.privateMode ? "açık" : "kapalı"}
                        </span>
                      </li>
                      <li>
                        <span className="text-fg-subtle">Aciliyet:</span>{" "}
                        <span className="text-fg-strong">
                          {wizard.urgency
                            ? URGENCY_OPTIONS.find(
                                (o) => o.value === wizard.urgency,
                              )?.label ?? wizard.urgency
                            : "otomatik"}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="glass rounded-xl border border-white/[0.06] p-4">
                    <header className="mb-2 flex items-center justify-between">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                        Konsey ({council.count})
                      </h4>
                      <button
                        type="button"
                        onClick={() => guardedSetStep(3)}
                        className="text-xs text-accent-300 hover:underline"
                      >
                        Düzenle
                      </button>
                    </header>
                    <p className="mb-2 text-[10px] uppercase tracking-wider text-fg-subtle">
                      {council.sourceLabel}
                    </p>
                    {council.resolving ? (
                      <p className="text-xs text-fg-subtle">Konsey yükleniyor…</p>
                    ) : council.count === 0 ? (
                      <p className="text-xs text-amber-300">
                        Üye yok — 3. adıma dönüp konsey seç.
                      </p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {council.members.slice(0, 8).map((p) => (
                          <li key={p.id} className="flex items-center gap-2 text-fg-muted">
                            <span
                              aria-hidden
                              className="h-2 w-2 flex-none rounded-full"
                              style={{ background: p.themeColor }}
                            />
                            <span className="truncate text-fg-strong">{p.displayName}</span>
                            {p.memoryCount > 0 && (
                              <span
                                className="flex-none rounded-full border border-accent-300/30 px-1 text-[9px] text-accent-300/90"
                                title={`${p.memoryCount} hafıza notuyla geliyor`}
                              >
                                {p.memoryCount}
                              </span>
                            )}
                            <span
                              className="ml-auto max-w-[9rem] truncate text-[10px] text-fg-subtle"
                              title={p.expertise || roleLabel(p.role)}
                            >
                              {p.expertise?.trim() || roleLabel(p.role)}
                            </span>
                          </li>
                        ))}
                        {council.count > 8 && (
                          <li className="text-[10px] text-fg-subtle">
                            +{council.count - 8} üye daha
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                </section>

                {/* Ortak Bilgi Tabanı (Pool RAG) — wizard'ın son adımında
                    yer alıyor ki kullanıcı topolojiyi/üyeleri bitirdikten
                    sonra "tüm konseyin göreceği belgeler" mental modeli
                    oturmuş olsun. `personaId` omitted ⇒ pool scope. */}
                <section className="glass rounded-xl border border-white/[0.06] p-4">
                  <header className="mb-3 flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                        Ortak Bilgi Tabanı
                      </h4>
                      <p className="mt-0.5 text-[11px] text-fg-subtle">
                        Buraya yüklediğin belgeler{" "}
                        <strong className="text-fg-muted">tüm konsey</strong>{" "}
                        tarafından RAG olarak kullanılır.
                      </p>
                    </div>
                  </header>
                  <RagDropZone
                    scopeLabel="Belge bırak ya da seç"
                    helpText="Konseye genel arka plan ver."
                  />
                </section>

                {/* Üye Belgeleri (Per-persona RAG) — accordion: her üye
                    için ayrı dropzone, default kapalı. Açılan satırın
                    body'si lazy render edilir ⇒ 6-üyelik bir konseyde
                    `listDocuments` 6 paralel istek değil, kullanıcı
                    açtıkça 1'er istek atılır. */}
                <section className="glass rounded-xl border border-white/[0.06] p-4">
                  <header className="mb-3 flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                        Üye Belgeleri (Kişiye Özel)
                      </h4>
                      <p className="mt-0.5 text-[11px] text-fg-subtle">
                        Yalnız ilgili üyenin bağlamına eklenir; başka
                        üyeler bu kaynakları görmez. Brifing, sözleşme
                        metni, gizli rapor gibi sadece tek bir uzmanın
                        savunmasına dayanak olacak belgeler için.
                      </p>
                    </div>
                  </header>
                  {council.isLibrary ? (
                    // Kütüphane personaları tartışma anında YENİ bir runtime
                    // uuid alır; buradaki yükleyici o üretilmemiş kimliğe
                    // bağlanır ve belge hiçbir turda okunmazdı. Kalıcı
                    // kimliklerin belgeleri kütüphanedeki kendi arşivlerinde
                    // yaşar ve her tartışmaya otomatik gelir.
                    <p className="rounded-lg border border-accent-300/25 bg-[hsl(var(--accent)/0.05)] px-3 py-2 text-[11.5px] leading-relaxed text-fg-muted">
                      Kalıcı üyelerin kişisel belgeleri{" "}
                      <strong className="text-fg-strong">Persona Kütüphanesi</strong>{" "}
                      içindeki kendi arşivlerinde tutulur ve katıldıkları her
                      tartışmaya otomatik gelir. Belge eklemek için ilgili
                      personayı kütüphaneden açıp “Arşiv” sekmesini kullan.
                    </p>
                  ) : (
                    <PersonaRagAccordion personas={wizard.personas} />
                  )}
                </section>

                <section className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-surface-2/40 px-4 py-3 text-xs text-fg-muted">
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4 text-fg-subtle" strokeWidth={1.5} />
                    <span>
                      ~{cost.approxSeconds}s · ~
                      {cost.approxTokens.toLocaleString("tr-TR")} token
                    </span>
                    {/* The council reads what is attached to the id this
                        debate launches under — not what the step-2
                        dropzone happens to be listing. Showing the count
                        here is the only place an operator can catch a
                        detached upload before the debate runs. */}
                    <span
                      className={
                        stagedDocCount > 0
                          ? "flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-300"
                          : "flex items-center gap-1.5 rounded-md border border-white/[0.06] px-2 py-1 text-fg-subtle"
                      }
                      title={
                        stagedDocCount > 0
                          ? "Konseyin tamamı bu belgeleri okuyabilecek."
                          : "Bu tartışmaya bağlı bağlam belgesi yok. Belge eklemek istersen 2. adıma dön."
                      }
                    >
                      <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
                      {stagedDocCount > 0
                        ? `${stagedDocCount} belge ekli`
                        : "Belge yok"}
                    </span>
                  </div>
                  <Button
                    variant="primary"
                    onClick={onStart}
                    // BULUNAN HATA: koşul `wizard.personas.length === 0` idi.
                    // Kütüphane yolunda o dizi HER ZAMAN boş olduğundan düğme
                    // kalıcı olarak devre dışı kalıyor, tartışma hiç
                    // başlayamıyordu.
                    disabled={isLocked || council.resolving || council.count === 0}
                    size="lg"
                  >
                    <Rocket className="h-4 w-4" /> Tartışmayı Başlat
                  </Button>
                </section>
              </div>
            ) : null}
          </motion.div>
        </AnimatePresence>

        {/* Wizard nav */}
        <div className="mt-1 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={wizard.step === 1 || isLocked}
          >
            <ArrowLeft className="h-4 w-4" /> Geri
          </Button>
          {wizard.step < 4 ? (
            <Button
              variant="primary"
              onClick={() => void goNext()}
              loading={wizard.step === 1 && wizard.quality.status === "assessing"}
              disabled={!isStepValid(wizard.step, wizard) || isLocked}
            >
              İleri <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <span className="text-[11px] text-fg-subtle">
              Son adım — başlatmak için sağdaki düğmeyi kullan.
            </span>
          )}
        </div>
      </div>

      <PersonaEditDrawer
        open={drawerId !== null}
        personaId={drawerId}
        onClose={() => setDrawerId(null)}
      />
    </Card>
  );
}

export default SetupWizard;
